// Two tiers of "what we already knew", and a rule about what that is worth.
//
// Opening the Trains tab used to mean an empty board and a spinner while a
// request went to Melbourne and back. The board a commuter saw ninety seconds
// ago is nearly always the board they are about to see again, so it is shown
// FIRST, stamped with its own age, and the request runs behind it. That is the
// whole of this file: stale-while-revalidate, with the staleness on screen.
//
// WHY localStorage AND NOT IndexedDB. `src/core/storage.js` offers both. The
// `records` store is async, and an async read cannot paint the first frame —
// the board would flash empty and then fill, which is precisely the flicker
// this exists to remove. `prefs` is a synchronous localStorage proxy, so the
// last board is on screen before Vue has finished mounting. Size is not the
// constraint anyone feared: a thirty-row board is about 6 KB of JSON and the
// cap is five stops, so roughly 30 KB against a 5 MB budget.
//
// WHAT THIS IS NOT. It is not a fallback for truth. A cached board is shown as
// a cached board, with the time it was read; `fresh` is the only thing allowed
// to skip a request, and only for the few seconds in which a second request
// would return the identical answer. Nothing here invents a train.

import { prefs } from './core/storage.js';

const ENTRY = 'cache:';
const INDEX = 'cache-index:';

// Tier one. Survives navigation within a session, not a relaunch.
const memory = new Map();

const entryKey = (key) => ENTRY + key;

// Keys are written `namespace:id` — `board:1034`. The namespace is what a cap
// counts, so five stops means five `board:` entries and says nothing about any
// other kind of thing that is cached later.
const namespaceOf = (key) => String(key).split(':')[0];

function readStored(key) {
  const row = prefs[entryKey(key)];
  if (!row || typeof row !== 'object') return null;
  const at = Number(row.t);
  if (!Number.isFinite(at)) return null;
  return { value: row.v, fetchedAt: at };
}

/**
 * What we have for this key, and whether it is new enough to trust unasked.
 *
 * Always answers — a miss is `{ value: null, fetchedAt: null, fresh: false }`
 * rather than undefined, so a caller never has to guard before reading
 * `.value`. `fresh` is false for a miss, for an expired entry, and for an entry
 * stamped in the future (a phone whose clock moved backwards is not evidence of
 * anything).
 */
export function cached(key, ttlMs = 0) {
  let hit = memory.get(key) ?? null;
  if (!hit) {
    hit = readStored(key);
    if (hit) memory.set(key, hit);
  }
  if (!hit) return { value: null, fetchedAt: null, fresh: false };

  const age = Date.now() - hit.fetchedAt;
  return {
    value: hit.value,
    fetchedAt: hit.fetchedAt,
    fresh: age >= 0 && age < Number(ttlMs || 0),
  };
}

/**
 * Keep an answer, in both tiers, stamped with when it was true.
 *
 * `cap` bounds the namespace: the most recently remembered `cap` keys survive
 * and the rest are dropped from storage entirely. Someone with five saved
 * stations should not be carrying twenty boards from stations they searched
 * once and never used.
 */
export function remember(key, value, { cap = 0, now = Date.now() } = {}) {
  const entry = { value, fetchedAt: now };
  memory.set(key, entry);
  prefs[entryKey(key)] = { v: value, t: now };
  if (cap > 0) capNamespace(namespaceOf(key), key, cap);
  return entry;
}

function capNamespace(ns, key, cap) {
  const idxKey = INDEX + ns;
  const keys = Array.isArray(prefs[idxKey]) ? prefs[idxKey] : [];
  const next = [key, ...keys.filter((k) => k !== key)];
  for (const dropped of next.slice(cap)) forget(dropped);
  prefs[idxKey] = next.slice(0, cap);
}

/** Drop one entry from both tiers. */
export function forget(key) {
  memory.delete(key);
  delete prefs[entryKey(key)];
}

/**
 * Drop the memory tier only, leaving what is persisted.
 *
 * What a relaunch does, without a relaunch — which is the only way to check
 * that the persisted tier is actually read rather than the memory one
 * answering every time.
 */
export function forgetSession() {
  memory.clear();
}
